<script setup>
  import {ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import PatientinputComponentAutocomplete from "@pages/Patientinput/Components/Autocomplete.vue";
  import PatientinputComponentStep from "@pages/Patientinput/Components/Step.vue";
  import {MedButton} from "@pharma4u/medicheck-components";
  import {coreRouter} from "@utils/coreRouter/coreRouter.js";

  defineOptions({
    name: "PatientinputComponentStepDiagnoses",
  });

  const props = defineProps({
    form: {
      type: Object,
      required: true,
    },
  });

  const refConfirmDeleteDialog = ref(null);

  const diagnoseToDelete = ref(null);

  const doSearch = (searchTerm) => {
    return new Promise((resolve) => {
      coreRouter.searchDiseasesForPatientinput(searchTerm, props.form.masterdata.patient.gender, {
        onSuccess: (response) => {
          resolve(response.data);
        },
        onError: () => {
          resolve(false);
        },
      });
    });
  };

  const diseaseSelected = (disease) => {
    if (!props.form.diagnoses.mivs.includes(disease.Key_MIV)) {
      props.form.diagnoses.mivs.push(disease.Key_MIV);
      props.form.diagnoses.list.push(disease);
    }
  };

  const confirmDelete = (disease) => {
    diagnoseToDelete.value = disease;
    refConfirmDeleteDialog.value.open();
  };

  const deleteConfirmed = () => {
    props.form.diagnoses.mivs = props.form.diagnoses.mivs.filter((miv) => miv !== diagnoseToDelete.value.Key_MIV);
    props.form.diagnoses.list = props.form.diagnoses.list.filter(
      ({Key_MIV}) => Key_MIV !== diagnoseToDelete.value.Key_MIV,
    );
    diagnoseToDelete.value = null;
  };
</script>

<template>
  <PatientinputComponentStep title="Diagnosen" optional :test-id="getTestId()">
    <div class="flex flex-col gap-8">
      <section><p>Hier können Sie Diagnosen des Patienten angeben.</p></section>

      <section>
        <PatientinputComponentAutocomplete
          placeholder="Nach Diagnose suchen"
          :fetch-method="doSearch"
          :test-id="getTestId('diagnoses-step')"
          @selected="diseaseSelected"
        >
          <template #autocomplete-item="{item}">
            <div class="flex min-h-7 items-center">
              {{ item.Name }}
            </div>

            <div v-if="form.diagnoses.mivs.some((miv) => miv === item.Key_MIV)" class="text-sm text-mcred-500">
              Diagnose wurde bereits hinterlegt
            </div>
          </template>
        </PatientinputComponentAutocomplete>
      </section>

      <section v-if="form.diagnoses.list.length > 0">
        <h2 class="text-2xl">Angegebene Diagnosen</h2>

        <ul class="flex flex-col gap-2">
          <li
            v-for="(disease, index) in form.diagnoses.list"
            :key="index"
            class="flex flex-row items-center justify-between gap-5"
          >
            <div>{{ disease.Name }}</div>

            <MedButton
              :aria-label="disease.name ? `Diagnose ${disease.name} löschen` : 'Diagnose löschen'"
              flat
              icon="delete"
              rounded="full"
              size="sm"
              :data-test="getTestId('diagnoses-step-delete-button')"
              @click="confirmDelete(disease)"
            />
          </li>
        </ul>
      </section>
    </div>

    <ComponentDialog
      ref="refConfirmDeleteDialog"
      dialog-title="Diagnose löschen?"
      primary-label="Ja, Löschen"
      :test-id="getTestId('diagnoses-step-delete')"
      @primary-button-clicked="deleteConfirmed"
    >
      <template #content>
        Sind Sie sicher, dass Sie die Diagnose „{{ diagnoseToDelete.Name }}“ löschen möchten?
      </template>
    </ComponentDialog>
  </PatientinputComponentStep>
</template>

<style scoped>
  @reference "@css/app.css";

  section {
    @apply flex flex-col gap-3;
  }
</style>
